import { ChatCircleText, EnvelopeSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { Switch } from '@/components/ui-kit/switch';
import { useToast } from '@/hooks/use-toast';
import { useUpdateContactSubscriptions } from '../../hooks/use-contacts';
import type { Contact } from '../../types/contacts.types';
import {
  buildChannelSubscriptionPatchRequest,
  type SubscriptionChannel,
} from '../../utils/contact-drawer-form.utils';
import type { ChannelStatusLabel } from '../../utils/contact-subscription-status';
import {
  getEmailChannelStatus,
  getSmsChannelStatus,
} from '../../utils/contact-subscription-status';
import { ContactSubscriptionStatusPill } from '../contact-subscription-status-pill/contact-subscription-status-pill';

type ContactSubscriptionPanelProps = {
  contact: Contact;
};

const CHANNEL_CONFIG: Record<
  SubscriptionChannel,
  { label: string; icon: typeof EnvelopeSimple }
> = {
  Email: {
    label: 'Email',
    icon: EnvelopeSimple,
  },
  Sms: {
    label: 'SMS',
    icon: ChatCircleText,
  },
};

const getNextStatusLabel = (status: ChannelStatusLabel): ChannelStatusLabel =>
  status === 'Subscribed' ? 'Unsubscribed' : 'Subscribed';

const ChannelRow = ({
  channel,
  status,
  isUpdating,
  onToggle,
}: {
  channel: SubscriptionChannel;
  status: ChannelStatusLabel;
  isUpdating: boolean;
  onToggle: (channel: SubscriptionChannel, status: ChannelStatusLabel) => void;
}) => {
  const { label, icon: Icon } = CHANNEL_CONFIG[channel];

  if (status === 'Suppressed') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900">
            <Icon className="size-3.5 text-neutral-500" aria-hidden />
            {label}
          </span>
          <ContactSubscriptionStatusPill status="Suppressed" />
        </div>
        <p className="text-xs text-neutral-500">
          On the suppression list for this channel — re-subscribing isn&apos;t allowed from here.
        </p>
      </div>
    );
  }

  if (status === 'Pending') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900">
            <Icon className="size-3.5 text-neutral-500" aria-hidden />
            {label}
          </span>
          <ContactSubscriptionStatusPill status="Pending" />
        </div>
        <p className="text-xs text-neutral-500">
          Awaiting the contact&apos;s double opt-in confirmation — set automatically, not togglable
          here.
        </p>
      </div>
    );
  }

  const isSubscribed = status === 'Subscribed';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-900">
          <Icon className="size-3.5 text-neutral-500" aria-hidden />
          {label}
        </span>
        <Switch
          checked={isSubscribed}
          disabled={isUpdating}
          aria-label={`${label} subscription`}
          onCheckedChange={() => onToggle(channel, status)}
        />
      </div>
      <p className="text-xs text-neutral-500">{isSubscribed ? 'Subscribed' : 'Unsubscribed'}</p>
    </div>
  );
};

export const ContactSubscriptionPanel = ({ contact }: ContactSubscriptionPanelProps) => {
  const { toast } = useToast();
  const { mutateAsync: updateSubscriptions } = useUpdateContactSubscriptions();
  const [updatingChannel, setUpdatingChannel] = useState<SubscriptionChannel | null>(null);

  const emailStatus = getEmailChannelStatus(contact);
  const smsStatus = getSmsChannelStatus(contact);

  const handleToggle = async (channel: SubscriptionChannel, status: ChannelStatusLabel) => {
    const patch = buildChannelSubscriptionPatchRequest(channel, status);
    if (!patch) return;

    const config = CHANNEL_CONFIG[channel];
    const nextLabel = getNextStatusLabel(status);

    setUpdatingChannel(channel);
    try {
      await updateSubscriptions({
        contactId: contact.ItemId,
        input: patch,
      });
      toast({
        variant: 'success',
        title: `${config.label} set to ${nextLabel}.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: error instanceof Error ? error.message : 'Could not update subscription status.',
      });
    } finally {
      setUpdatingChannel(null);
    }
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Subscription &amp; consent
      </div>
      <div className="flex flex-col gap-4">
        {emailStatus ? (
          <ChannelRow
            channel="Email"
            status={emailStatus}
            isUpdating={updatingChannel === 'Email'}
            onToggle={handleToggle}
          />
        ) : null}
        {smsStatus ? (
          <ChannelRow
            channel="Sms"
            status={smsStatus}
            isUpdating={updatingChannel === 'Sms'}
            onToggle={handleToggle}
          />
        ) : null}
      </div>
    </div>
  );
};
